import React, { FC } from 'react';
import {
  AvatarSize,
  Box,
  FormControl,
  Icon,
  IconSize,
  MultiSelect,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { useField } from 'formik';
import { useName } from '@bechtel/client-common';
import { AvatarRegularStyled, MultiSelectedItemStyled } from './styled';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { StencilSelectorProps } from './types';
import { orderBy } from 'lodash';

const StencilItem = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '300' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box flexDirection={{ sm: 'column' }} mr={{ sm: '300' }}>
        {item.badgeNumber && (
          <Box mb={{ sm: '200' }}>
            <Text size="300" color="grey.600">
              {item.badgeNumber}
            </Text>
          </Box>
        )}
        <Text size="200" color="grey.1000" transform="capitalize" style={{ opacity: '0.7' }}>
          {getFullName(item.firstName, item.lastName)}
        </Text>
      </Box>
    </Box>
  );
};

const StencilSelectedItem = ({ item, removeItem }: any) => {
  const { getFullName } = useName();

  return (
    <MultiSelectedItemStyled>
      <Box mr={{ sm: '300' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box flexDirection={{ sm: 'column' }} mr={{ sm: '300' }}>
        {item.badgeNumber && (
          <Text size="300" color="grey.600">
            {item.badgeNumber}
          </Text>
        )}
        {!item.badgeNumber && (
          <Text size="200" color="grey.1000" transform="capitalize" style={{ opacity: '0.7' }}>
            {getFullName(item.firstName, item.lastName)}
          </Text>
        )}
      </Box>
      <Icon
        color="white"
        icon="times-circle"
        fillColor="blue.800"
        size={IconSize.SMALL}
        onClick={(e) => removeItem(e, item)}
      ></Icon>
    </MultiSelectedItemStyled>
  );
};

export const StencilSelector: FC<StencilSelectorProps> = ({
  loading,
  items,
  fieldName,
  onSelectedItemsChange,
  onSearch,
  hideDropDownIcon,
  formLabel,
  placeholder,
  enableClientSearch,
}) => {
  const { i18n } = useLingui();
  const [field, meta, helpers] = useField(fieldName);
  const { setValue } = helpers;
  const orderedCrewMemberList = items.length > 0 ? orderBy(items, 'badgeNumber') : [];

  return (
    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
      <FormControl label={formLabel ? i18n._(formLabel) : ''}>
        <MultiSelect
          {...field}
          hideDropDownIcon={hideDropDownIcon}
          placeholder={placeholder ? i18n._(placeholder) : t`Add Stencil`}
          items={orderedCrewMemberList}
          itemComponent={StencilItem}
          selectedComponent={StencilSelectedItem}
          selectedItems={field.value}
          size={TextFieldSize.LARGE}
          displayKeyField="firstName"
          appearance={TextFieldAppearance.NORMAL}
          loading={loading}
          enableClientSearch={enableClientSearch}
          onSearch={(query: string) => {
            onSearch && onSearch(query);
          }}
          onSelectedItemsChange={async (changes) => {
            const { selectedItems } = changes;
            if (!selectedItems) return;
            setValue([...selectedItems]);
            if (!onSelectedItemsChange) return;
            onSelectedItemsChange([...selectedItems]);
          }}
        />
      </FormControl>
      {meta.touched && meta.error ? (
        <Box pt={{ sm: '200' }}>
          <Text color="bechtel.red">{i18n._(meta.error)}</Text>
        </Box>
      ) : null}
    </Box>
  );
};
